import MenuTop from "../../../components/menu/top/menu-top";
import { useParams } from "react-router-dom";
import { fetchEditProjectService } from "../../../features/project/service/project-service";
import { createRubricaService } from "../../../features/rubrica/service/rubrica";
import { CreateRubricaPayload, RubricaTypeOption } from "../../../components/modals/rubrica/create-rubrica-modal";
import { PendingRubricaOption } from "../../../components/modals/rubrica/rubrica-modal";
import { useCallback, useEffect, useState } from "react";
import { createFinancierService, updateFinancierService } from "../../../features/financial/service/financiador-service";
import { useNavigate } from "react-router-dom";
import { projectData } from "../../../features/project/forms/project-data";
import { useAlert } from "../../../components/alert/alert";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { Project } from "../../../shared/interfaces/project";
import { FormProps, FormTypes } from "../../../shared/interfaces/form";
import Form from "../../../components/form/form";
import FinanceTimeline from "../../../components/timeline/timeline";
import InfoComponent from "../../../components/info/info";
import { SelectActions } from "../../../shared/interfaces/select";
import {
    fetchCachedCompaniesService,
    fetchCachedFinanciersService,
    fetchCachedProjectFinancialTimelineService,
    fetchCachedProjectService,
    fetchCachedRubricaTypesService,
    fetchCachedSearchersListService,
    fetchCachedUserCompanyService,
    invalidateProjectDetailCache,
    invalidateProjectSupportCache,
} from "../../../features/project/service/project-query-cache";

interface PendingFinancierAction {
    type: 'create' | 'edit';
    optionId: string;
    persistedId?: string;
    payload: {
        name: string;
        description: string;
        idCompany: string;
    };
}

function ProjectEdit() {
    const { id } = useParams();
    const [ formData, setFormData ] = useState<FormProps[] | any>();
    const [ formSections, setFormSections ] = useState<any[]>();
    const { showAlert } = useAlert();
    const [ timelineData, setTimelineData ] = useState<any[]>();
    const [ company, setCompany ] = useState<any>();
    const [ isLoading, setIsLoading ] = useState(true);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ pendingFinancierAction, setPendingFinancierAction ] = useState<PendingFinancierAction | null>(null);
    const [ rubricaTypes, setRubricaTypes ] = useState<RubricaTypeOption[]>([]);
    const [ pendingRubricas, setPendingRubricas ] = useState<PendingRubricaOption[]>([]);
    const navigate = useNavigate();

    const handleCreatePendingRubrica = useCallback((payload: CreateRubricaPayload): PendingRubricaOption => {
        const tempId = `pending-rubrica-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const matchedType = payload.typeId
            ? rubricaTypes.find((type) => type.id === payload.typeId)
            : undefined;

        const pending: PendingRubricaOption = {
            tempId,
            name: payload.name,
            description: payload.description || payload.name,
            typeId: payload.typeId ?? null,
            typeName: matchedType?.name || 'Novas',
        };

        setPendingRubricas((current) => [...current, pending]);
        return pending;
    }, [rubricaTypes]);

    const mapFinancierToOption = useCallback((financier: any) => ({
        id: financier?.id,
        value: financier?.id,
        label: financier?.name,
        name: financier?.name,
        description: financier?.description,
        idCompany: financier?.idCompany ?? null,
    }), []);

    const createPendingFinancierOption = useCallback((name: string, idCompany: string, optionId?: string) => {
        const temporaryId = optionId || `pending-financier-${Date.now()}`;

        return mapFinancierToOption({
            id: temporaryId,
            name,
            description: name,
            idCompany,
        });
    }, [mapFinancierToOption]);

    const fetchSearchers = useCallback(async () => {
        try {
            const response = await fetchCachedSearchersListService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching searchers:", error);
            return [];
        }
    }, []);

    const fetchFinanciers = useCallback(async () => {
        try {
            const response = await fetchCachedFinanciersService();
            return response?.map(mapFinancierToOption) || [];
        } catch (error) {
            console.error("Error fetching financiers:", error);
            return [];
        }
    }, [mapFinancierToOption]);

    const fetchListCompanies = useCallback(async () => {
        try {
            const response = await fetchCachedCompaniesService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching companies:", error);
            return [];
        }
    }, []);

    const fetchUserCompany = useCallback(async () => {
        try {
            return await fetchCachedUserCompanyService();
        } catch (error) {
            console.error("Error fetching user company:", error);
            return null;
        }
    }, []);

    // Função para organizar os dados em seções (SEM movements e balance para edição)
    const organizeFormDataIntoSections = (data: FormProps[]) => {
        const projectFields = ['name', 'principalInvestigatorId', 'initialDate', 'finalDate', 'duration'];
        const financialFields = ['financierId', 'accountabilittyDate', 'valor', 'multiplePayments', 'multiplePaymentsRecurrency'];
        const clientFields = ['companyId'];

        const projectData = data.filter(field => projectFields.includes(field.name));
        const financialData = data.filter(field => financialFields.includes(field.name));
        const clientData = data.filter(field => clientFields.includes(field.name));

        return [
            {
                title: "Informações do Projeto",
                icon: "📋",
                color: "project",
                fields: projectData
            },
            {
                title: "Informações Financeiras", 
                icon: "💰",
                color: "financial",
                fields: financialData
            },
            {
                title: "Informações do Cliente",
                icon: "🏢", 
                color: "client",
                fields: clientData
            }
        ];
    };

    const fetchAllData = useCallback(async (projectId: string) => {
        setIsLoading(true);
        try {
            const response = await fetchCachedProjectService(projectId);
            const { principalInvestigator, financier, initialDate, finalDate, companyId, ...restResponse } = response;
            
            const [searchers, financiers, companies, userCompany, financialTimeline, rubricaTypesResponse] = await Promise.all([
                fetchSearchers(),
                fetchFinanciers(),
                fetchListCompanies(),
                fetchUserCompany(),
                fetchCachedProjectFinancialTimelineService(projectId),
                fetchCachedRubricaTypesService().catch(() => [])
            ]);

            setCompany(userCompany);
            setTimelineData(financialTimeline);
            setRubricaTypes(
                Array.isArray(rubricaTypesResponse)
                    ? rubricaTypesResponse.map((type: any) => ({ id: type.id, name: type.name }))
                    : []
            );

            const financierActions: SelectActions = {
                create: {
                    label: 'Adicionar financiador',
                    modalTitle: 'Adicionar financiador',
                    confirmLabel: 'Adicionar',
                    onSubmit: async (name: string) => {
                        if (!userCompany?.id) {
                            throw new Error('Empresa do usuário não encontrada');
                        }

                        const companyIdToUse = userCompany.id;
                        const pendingOption = createPendingFinancierOption(name, companyIdToUse);

                        setPendingFinancierAction({
                            type: 'create',
                            optionId: pendingOption.id,
                            payload: {
                                name,
                                description: name,
                                idCompany: companyIdToUse,
                            }
                        });

                        showAlert('Financiador será criado ao salvar o projeto', SEVERITY.INFO);
                        return pendingOption;
                    }
                },
                edit: {
                    label: 'Editar financiador selecionado',
                    modalTitle: 'Editar financiador',
                    confirmLabel: 'Salvar',
                    canShow: (option: any) => option?.idCompany === userCompany?.id,
                    onSubmit: async (option: any, name: string) => {
                        if (!userCompany?.id) {
                            throw new Error('Empresa do usuário não encontrada');
                        }

                        const companyIdToUse = userCompany.id;
                        const optionId = option?.id || option?.value;
                        setPendingFinancierAction((currentAction) => {
                            if (currentAction?.type === 'create' && currentAction.optionId === optionId) {
                                return {
                                    ...currentAction,
                                    payload: {
                                        name,
                                        description: name,
                                        idCompany: companyIdToUse,
                                    }
                                };
                            }

                            return {
                                type: 'edit',
                                optionId,
                                payload: {
                                    name,
                                    description: name,
                                    idCompany: companyIdToUse,
                                }
                            };
                        });

                        showAlert('Alteração do financiador será salva junto com o projeto', SEVERITY.INFO);
                        return createPendingFinancierOption(name, companyIdToUse, optionId);
                    }
                }
            };

            // Verificar se existe multiplePaymentsRecurrency e definir multiplePayments
            const hasMultiplePaymentsRecurrency = response.multiplePaymentsRecurrency != null && response.multiplePaymentsRecurrency !== '';
            if (hasMultiplePaymentsRecurrency) {
                restResponse.multiplePayments = true;
            }

            const data = projectData(
                projectId, 
                restResponse, 
                principalInvestigator || {}, 
                searchers || [], 
                financiers || [], 
                initialDate, 
                finalDate,
                financier,
                companyId,
                companies,
                userCompany?.name,
                financialTimeline, // Passando o timelineData
                financierActions
            );

            // FILTRAR campos de saldo e movimentações para edição
            const filteredData = data.filter(field => 
                field.name !== 'movements' && 
                field.name !== 'balance'
            );

            // Se tem multiplePaymentsRecurrency, adicionar o campo após o checkbox
            if (hasMultiplePaymentsRecurrency) {
                const checkboxIndex = filteredData.findIndex(item => item.name === 'multiplePayments');
                if (checkboxIndex !== -1) {
                    const newField = {
                        typeOfField: FormTypes.INPUT,
                        type: 'number',
                        placeholder: 'Recorrência dos pagamentos (em meses)*',
                        rules: {
                            required: 'Recorrência dos pagamentos é obrigatória',
                            min: {
                                value: 1,
                                message: 'A recorrência deve ser pelo menos 1 mês'
                            }
                        },
                        value: response.multiplePaymentsRecurrency,
                        name: 'multiplePaymentsRecurrency',
                        width: 200,
                        height: 55,
                        disabled: false
                    };
                    
                    filteredData.splice(checkboxIndex + 1, 0, newField);
                }
            }

            if (response.rubricas && response.rubricas.length > 0) {
                filteredData.push({
                    typeOfField: FormTypes.HIDDEN,
                    name: 'rubricas',
                    placeholder: 'Rubricas',
                    value: response.rubricas
                });
            }

            setFormData(filteredData);
            
            // Organizar dados em seções
            const sections = organizeFormDataIntoSections(filteredData);
            setFormSections(sections);

        } catch (error) {
            console.error("Error fetching project data:", error);
            showAlert('Erro ao carregar dados do projeto', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [createPendingFinancierOption, fetchSearchers, fetchFinanciers, fetchListCompanies, fetchUserCompany, showAlert]);

    const handleFieldChange = useCallback((fieldName: string, value: any) => {
        // O react-hook-form já mantém o estado do formulário.
        // Reescrever formData aqui a cada tecla acaba disparando novos resets no Form.
    }, []);

    const handleCheckboxChange = useCallback((value: boolean, fieldName: string) => {
        if (fieldName === 'multiplePayments') {
            setFormData((prevFormData: FormProps[]) => {
                if (!prevFormData || !prevFormData.length) return prevFormData;
                
                // Primeiro, atualizar o valor do checkbox
                const updatedFormData = prevFormData.map(item => {
                    if (item.name === 'multiplePayments') {
                        return { ...item, value: value };
                    }
                    return item;
                });
                
                // Encontrar o índice do campo multiplePayments
                const checkboxIndex = updatedFormData.findIndex(item => item.name === 'multiplePayments');
                
                let finalFormData;
                
                if (value) {
                    // Adicionar campo - verificar se já existe
                    const fieldExists = updatedFormData.some((item: FormProps) => item.name === 'multiplePaymentsRecurrency');
                    
                    if (!fieldExists && checkboxIndex !== -1) {
                        const newField: FormProps = {
                            typeOfField: FormTypes.INPUT,
                            type: 'number',
                            placeholder: 'Recorrência dos pagamentos (em meses)*',
                            rules: {
                                required: 'Recorrência dos pagamentos é obrigatória',
                                min: {
                                    value: 1,
                                    message: 'A recorrência deve ser pelo menos 1 mês'
                                }
                            },
                            value: null,
                            name: 'multiplePaymentsRecurrency',
                            width: 200,
                            height: 55,
                            disabled: false
                        };
                        
                        // Inserir após o campo multiplePayments
                        const insertIndex = checkboxIndex + 1;
                        
                        finalFormData = [
                            ...updatedFormData.slice(0, insertIndex),
                            newField,
                            ...updatedFormData.slice(insertIndex)
                        ];
                    } else {
                        finalFormData = updatedFormData;
                    }
                } else {
                    // Remover campo se existir
                    finalFormData = updatedFormData.filter(item => item.name !== 'multiplePaymentsRecurrency');
                }
                
                // Reorganizar seções após mudança
                const sections = organizeFormDataIntoSections(finalFormData);
                setFormSections(sections);
                
                return finalFormData;
            });
        }
    }, []);
    
    const onSubmitForm = async (data: Project) => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setIsLoading(true);

        try {
            const projectDataToSubmit = data as Project & { financierId?: string };
            projectDataToSubmit.subProject = null;

            if (pendingFinancierAction && projectDataToSubmit.financierId === pendingFinancierAction.optionId) {
                if (pendingFinancierAction.type === 'create') {
                    if (pendingFinancierAction.persistedId) {
                        projectDataToSubmit.financierId = pendingFinancierAction.persistedId;
                    } else {
                        const createdFinancier = await createFinancierService(pendingFinancierAction.payload);
                        projectDataToSubmit.financierId = createdFinancier.id;
                        invalidateProjectSupportCache();
                        setPendingFinancierAction((currentAction) => currentAction ? {
                            ...currentAction,
                            persistedId: createdFinancier.id,
                        } : currentAction);
                    }
                } else {
                    await updateFinancierService(pendingFinancierAction.optionId, pendingFinancierAction.payload);
                    invalidateProjectSupportCache();
                }
            }

            if (company?.name?.toLocaleLowerCase() !== 'causale') {
                projectDataToSubmit.companyId = company.id;
            }

            // Se multiplePayments for false, garantir que multiplePaymentsRecurrency seja 0
            if (!projectDataToSubmit.multiplePayments) {
                projectDataToSubmit.multiplePaymentsRecurrency = 0;
            }

            // Persistir rubricas criadas inline antes de salvar o projeto
            const submittedRubricas = (projectDataToSubmit as any).rubricas as Array<any> | undefined;
            if (Array.isArray(submittedRubricas) && submittedRubricas.length > 0 && pendingRubricas.length > 0) {
                const tempIds = new Set(pendingRubricas.map((p) => p.tempId));
                const referenced = submittedRubricas.filter((r) => tempIds.has(r.rubricaId));

                if (referenced.length > 0) {
                    const idMap = new Map<string, string>();
                    for (const pending of pendingRubricas) {
                        if (!referenced.some((r) => r.rubricaId === pending.tempId)) continue;
                        const created = await createRubricaService({
                            name: pending.name,
                            description: pending.description,
                            typeId: pending.typeId ?? null,
                            clientId: company?.id,
                        });
                        invalidateProjectSupportCache();
                        idMap.set(pending.tempId, created.id);
                    }

                    (projectDataToSubmit as any).rubricas = submittedRubricas.map((r) => {
                        const realId = idMap.get(r.rubricaId);
                        if (!realId) return r;
                        return {
                            ...r,
                            rubricaId: realId,
                            rubrica: r.rubrica ? { ...r.rubrica, id: realId } : r.rubrica,
                        };
                    });
                }
            }

            await fetchEditProjectService(id as string, projectDataToSubmit);
            invalidateProjectDetailCache(id as string);
            setPendingFinancierAction(null);
            setPendingRubricas([]);

            showAlert('Projeto editado com sucesso', SEVERITY.SUCCESS);
            if (id) {
                await fetchAllData(id);
            }
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao editar projeto', 
                SEVERITY.ERROR
            );
        } finally {
            setIsLoading(false);
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        if (id) { fetchAllData(id)}

    }, [id, fetchAllData]);

    return (
        <div className="container-show-project">
            <MenuTop />
            <div className="container-show-project__header">
            </div>
            <InfoComponent />
            <div className="container-show-project__container">
                <Form
                    data={formData}
                    sections={formSections}
                    title='Editar projeto'
                    onSubmitForm={onSubmitForm}
                    onCancel={() => navigate('/dashboard', {replace: true})}
                    isLoading={isLoading}
                    showFinancial={true}
                    onChangeCheckbox={handleCheckboxChange}
                    onFieldValueChange={handleFieldChange}
                    rubricaTypes={rubricaTypes}
                    pendingRubricas={pendingRubricas}
                    onCreatePendingRubrica={handleCreatePendingRubrica}
                    onClickedMenuContextType={(type: string) => {
                        if (type === 'financial') {
                            navigate(`/financeiro/${id}/adicionar-projeto`, {replace: true});
                        }
                    }}
                />
                <FinanceTimeline data={timelineData as []}/>
            </div>
        </div>
    );
}

export default ProjectEdit;
